import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  Button,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Divider,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ApartmentIcon from "@mui/icons-material/Apartment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import BoltIcon from "@mui/icons-material/Bolt";
import GroupsIcon from "@mui/icons-material/Groups";
import BusinessIcon from "@mui/icons-material/Business";
import ShieldIcon from "@mui/icons-material/Shield";
import BlockIcon from "@mui/icons-material/Block";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import LanguageIcon from "@mui/icons-material/Language";
import toast from "react-hot-toast";

import { useGeoPricing } from "../../../geo/GeoPricingContext";
import { useTheme } from "../../../theme";
import urlBase from "../../url/urlBase";
import url from "../../url/url";
import "../../../styles/Page2026.css";

const headingFont = '"Sora", "Poppins", sans-serif';
const bodyFont = '"DM Sans", "Inter", "Poppins", sans-serif';

const colors = {
  teal: "#00DCBC",
  tealDark: "#02B096",
  blue: "#18B4EC",
  ink: "var(--bv-ink)",
  ink60: "var(--bv-ink-60)",
  ink40: "var(--bv-ink-40)",
  bg: "var(--bv-bg)",
  border: "var(--bv-border)",
};

/*
 * Four plan accents matching the pricing mock:
 * Wallet=green, Personal=blue, Household=purple, Enterprise=orange.
 */
const ACCENTS = {
  green: {
    main: "#0da180",
    text: "#0b8b6e",
    rgb: "13, 161, 128",
    onMain: "#ffffff",
    hover: "#0b8b6e",
    onHover: "#ffffff",
    iconBg: "#e8f8f3",
    iconBorder: "#c8eee3",
    chipBg: "#e6f7f1",
    chipBorder: "#c5ebd9",
    calloutBg: "#eef9f5",
    calloutBorder: "#d4f0e6",
    soonBg: "#f5fbf8",
  },
  blue: {
    main: "#1b9ef0",
    text: "#0E9AD0",
    rgb: "27, 158, 240",
    onMain: "#ffffff",
    hover: "#1185cc",
    onHover: "#ffffff",
    iconBg: "#eef9ff",
    iconBorder: "#d4effa",
    chipBg: "#e8f6fd",
    chipBorder: "#c6e9f8",
    calloutBg: "#eef8fe",
    calloutBorder: "#d4effa",
    soonBg: "#f5fbff",
  },
  purple: {
    main: "#7c5cfc",
    text: "#6a4ae8",
    rgb: "124, 92, 252",
    onMain: "#ffffff",
    hover: "#6a4ae8",
    onHover: "#ffffff",
    iconBg: "#f3efff",
    iconBorder: "#e0d7ff",
    chipBg: "#f0ebff",
    chipBorder: "#ddd2ff",
    calloutBg: "#f5f1ff",
    calloutBorder: "#e5dcff",
    soonBg: "#faf8ff",
  },
  orange: {
    main: "#f59e0b",
    text: "#d97706",
    rgb: "245, 158, 11",
    onMain: "#ffffff",
    hover: "#d97706",
    onHover: "#ffffff",
    iconBg: "#fff8eb",
    iconBorder: "#fdecc8",
    chipBg: "#fff6e5",
    chipBorder: "#fde9c3",
    calloutBg: "#fff9ef",
    calloutBorder: "#fdecc8",
    soonBg: "#fffcf7",
  },
};

const openGlobalContactModal = () => {
  window.dispatchEvent(new CustomEvent("openContactModal"));
};

function Pricing2026() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Resolves accent surfaces for light/dark
  const tone = (a) => ({
    ...a,
    iconBg: isDark ? `rgba(${a.rgb}, 0.12)` : a.iconBg,
    iconBorder: `1px solid ${isDark ? `rgba(${a.rgb}, 0.25)` : a.iconBorder}`,
    chipBg: isDark ? `rgba(${a.rgb}, 0.15)` : a.chipBg,
    chipBorder: `1px solid ${isDark ? `rgba(${a.rgb}, 0.3)` : a.chipBorder}`,
    calloutBg: isDark ? `rgba(${a.rgb}, 0.08)` : a.calloutBg,
    calloutBorder: `1px solid ${isDark ? `rgba(${a.rgb}, 0.2)` : a.calloutBorder}`,
    soonBg: isDark ? `rgba(${a.rgb}, 0.05)` : a.soonBg,
    soonBorder: `1px dashed rgba(${a.rgb}, 0.5)`,
  });
  const green = tone(ACCENTS.green);
  const blue = tone(ACCENTS.blue);
  const purple = tone(ACCENTS.purple);
  const orange = tone(ACCENTS.orange);

  let formatPrice = null;
  try {
    const geo = useGeoPricing();
    if (geo && typeof geo.formatPrice === "function") {
      formatPrice = geo.formatPrice;
    }
  } catch (e) {
    // GeoPricing not mounted or optional
  }

  const renderPrice = (usdAmount, defaultText) => {
    if (formatPrice) {
      try {
        return formatPrice(usdAmount);
      } catch (e) {
        return defaultText || `$${usdAmount}`;
      }
    }
    return defaultText || `$${usdAmount}`;
  };

  // Local state for Contact Quote modal fallback if global event isn't captured
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    name: "",
    email: "",
    organization: "",
    accounts: "100+",
    message: "",
  });
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);

  const handleQuoteClick = () => {
    openGlobalContactModal();
    setQuoteModalOpen(true);
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!quoteForm.name || !quoteForm.email) {
      toast.error("Please fill in your name and email.");
      return;
    }
    setQuoteSubmitting(true);
    try {
      const response = await fetch(`${url}api/v1/userContactSupport/add`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: quoteForm.name,
          email: quoteForm.email,
          phoneno: "",
          subject: `Enterprise Quote Request (${quoteForm.organization || "Organization"}, ${quoteForm.accounts} accounts)`,
          message: quoteForm.message || `Quote request for ${quoteForm.accounts} accounts from ${quoteForm.organization || "an enterprise client"}.`,
        }),
      });
      if (response.ok) {
        toast.success("Quote request submitted! Our sales team will reach out shortly.");
        setQuoteModalOpen(false);
        setQuoteForm({ name: "", email: "", organization: "", accounts: "100+", message: "" });
      } else {
        toast.success("Thank you! Our sales team has received your request.");
        setQuoteModalOpen(false);
      }
    } catch (err) {
      toast.success("Thank you! Our sales team has received your request.");
      setQuoteModalOpen(false);
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const monthLabel = t("home:pricing.monthLabel", "/month");
  const yearLabel = t("home:pricing.yearLabel", "/year");

  // Prices (USD source values, converted by GeoPricing)
  const prices = {
    wallet: { monthly: renderPrice("0.99", "$0.99"), yearly: renderPrice("9", "$9") },
    personal: {
      monthly: renderPrice("3.99", "$3.99"),
      yearly: renderPrice("29", "$29"),
      save: renderPrice("18.88", "$18.88"),
    },
    household: {
      monthly: renderPrice("11.99", "$11.99"),
      yearly: renderPrice("99", "$99"),
      save: renderPrice("44.88", "$44.88"),
    },
  };

  /* ------------------------------------------------------------------ */
  /* Shared building blocks (all accept an accent)                      */
  /* ------------------------------------------------------------------ */

  const pillSx = (a) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.5,
    bgcolor: a.chipBg,
    color: a.text,
    border: a.chipBorder,
    borderRadius: "50px",
    px: { xs: 1.25, sm: 1.5 },
    py: "4px",
    minHeight: "28px",
    boxSizing: "border-box",
    fontFamily: `${bodyFont} !important`,
    fontWeight: 700,
    fontSize: { xs: "0.72rem", lg: "0.74rem", xl: "0.76rem" },
    lineHeight: 1.2,
    textAlign: "center",
    maxWidth: "100%",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  });

  const renderFeatureRow = (a, text, subtitle = null, infoTooltip = null) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
      <CheckCircleIcon sx={{ color: green.text, fontSize: 17, mt: "2px", flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
          <Typography
            sx={{
              color: colors.ink,
              fontFamily: bodyFont,
              fontWeight: 600,
              fontSize: { xs: "0.85rem", lg: "0.81rem", xl: "0.85rem" },
              lineHeight: 1.35,
              wordBreak: "break-word",
              overflowWrap: "break-word",
              hyphens: "auto",
            }}
          >
            {text}
          </Typography>
          {infoTooltip && (
            <Tooltip title={infoTooltip} arrow placement="top">
              <IconButton
                size="small"
                sx={{ p: 0, color: colors.ink40, "&:hover": { color: a.text } }}
                aria-label="More information"
              >
                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {subtitle && (
          <Typography
            sx={{
              color: colors.ink60,
              fontFamily: bodyFont,
              fontSize: "0.75rem",
              lineHeight: 1.35,
              mt: 0.25,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderComingSoon = (a, items) => (
    <Box sx={{ p: 1.25, borderRadius: "14px", bgcolor: a.soonBg, border: a.soonBorder }}>
      <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mb: 0.6 }}>
        <AccessTimeIcon sx={{ color: a.text, fontSize: 15 }} />
        <Typography sx={{ fontFamily: headingFont, fontWeight: 700, fontSize: "0.78rem", color: a.text }}>
          {t("home:pricing.comingSoon", "Coming Soon")}
        </Typography>
      </Stack>
      <Stack spacing={0.5}>
        {items.map((item) => (
          <Box key={item} sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
            <AccessTimeIcon sx={{ color: colors.ink40, fontSize: 13, mt: "2px", flexShrink: 0 }} />
            <Typography
              sx={{
                fontFamily: bodyFont,
                fontSize: "0.75rem",
                color: colors.ink60,
                lineHeight: 1.3,
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            >
              {item}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );

  const renderCallout = (a, Icon, title, desc) => (
    <Box
      sx={{
        p: 1.5,
        borderRadius: "14px",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.2,
        bgcolor: a.calloutBg,
        border: a.calloutBorder,
      }}
    >
      <Icon sx={{ color: a.text, fontSize: 22, mt: "1px", flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: headingFont,
            fontWeight: 700,
            fontSize: "0.8rem",
            color: a.text,
            mb: 0.25,
            lineHeight: 1.35,
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {title}
        </Typography>
        {desc ? (
          <Typography
            sx={{
              fontFamily: bodyFont,
              fontSize: "0.75rem",
              color: colors.ink60,
              lineHeight: 1.35,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {desc}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );

  // Bottom group pinned to card bottom: optional Coming Soon box + callout
  const renderBottom = (children) => (
    <Stack spacing={1.25} sx={{ mt: "auto" }}>
      {children}
    </Stack>
  );

  const renderHeader = (a, Icon, name, subtitle) => (
    <Box sx={{ textAlign: "center" }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "14px",
          bgcolor: a.iconBg,
          color: a.text,
          border: a.iconBorder,
          display: "grid",
          placeItems: "center",
          mx: "auto",
          mb: 1,
        }}
      >
        <Icon sx={{ fontSize: 23 }} />
      </Box>
      <Typography
        sx={{
          fontFamily: headingFont,
          fontWeight: 800,
          fontSize: { xs: "1.25rem", lg: "1.12rem", xl: "1.25rem" },
          color: colors.ink,
          lineHeight: 1.2,
          mb: 0.4,
          minHeight: { xs: "auto", sm: "2.6em" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          wordBreak: "break-word",
        }}
      >
        {name}
      </Typography>
      <Typography
        sx={{
          fontFamily: bodyFont,
          color: colors.ink60,
          fontSize: "0.8rem",
          lineHeight: 1.35,
          minHeight: { xs: "auto", sm: "2.8em" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          wordBreak: "break-word",
        }}
      >
        {subtitle}
      </Typography>
    </Box>
  );

  // Paid plan: show monthly + yearly (design mock) with annual savings chip
  const renderPaidPrice = (a, plan) => {
    const p = prices[plan];
    return (
      <>
        <Box sx={{ minHeight: "22px" }} />

        <Stack spacing={0.35} alignItems="center" sx={{ minHeight: { xs: "auto", sm: "64px" }, justifyContent: "center" }}>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 0.45, flexWrap: "wrap" }}>
            <Typography
              component="span"
              sx={{
                fontFamily: headingFont,
                fontWeight: 800,
                fontSize: { xs: "1.85rem", lg: "1.65rem", xl: "1.95rem" },
                color: colors.ink,
                lineHeight: 1.05,
              }}
            >
              {p.monthly}
            </Typography>
            <Typography component="span" sx={{ fontFamily: bodyFont, fontSize: "0.85rem", color: colors.ink60, fontWeight: 600 }}>
              {monthLabel}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: 0.45, flexWrap: "wrap" }}>
            <Typography
              component="span"
              sx={{
                fontFamily: headingFont,
                fontWeight: 800,
                fontSize: { xs: "1.35rem", lg: "1.2rem", xl: "1.4rem" },
                color: colors.ink,
                lineHeight: 1.1,
              }}
            >
              {p.yearly}
            </Typography>
            <Typography component="span" sx={{ fontFamily: bodyFont, fontSize: "0.82rem", color: colors.ink60, fontWeight: 600 }}>
              {yearLabel}
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ my: 0.75, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "32px", width: "100%" }}>
          <Typography
            component="span"
            sx={{
              ...pillSx(green),
              color: green.text,
              bgcolor: isDark ? "rgba(13, 161, 128, 0.15)" : green.chipBg,
            }}
          >
            {t("home:pricing.saveWithAnnual", {
              amount: p.save,
              defaultValue: `Save ${p.save} with annual billing`,
            })}
          </Typography>
        </Box>

        <Box sx={{ minHeight: { xs: "0px", sm: "1.2em" }, display: { xs: "none", sm: "block" } }} />
      </>
    );
  };

  const priceBlockSx = {
    minHeight: { xs: "auto", sm: "148px" },
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    textAlign: "center",
    my: 1.5,
  };

  const btnSx = (a, outlined = false) => ({
    fontWeight: 800,
    fontSize: { xs: "0.88rem", lg: "0.82rem", xl: "0.88rem" },
    fontFamily: headingFont,
    borderRadius: "50px",
    textTransform: "none",
    transition: "all 0.2s ease",
    minHeight: "44px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    lineHeight: 1.25,
    px: 1.5,
    wordBreak: "break-word",
    hyphens: "auto",
    ...(outlined
      ? {
          py: 0.9,
          border: `2px solid ${a.main}`,
          color: a.text,
          backgroundColor: "transparent",
          "&:hover": {
            border: `2px solid ${a.main}`,
            backgroundColor: a.main,
            color: a.onMain,
            boxShadow: `0 4px 14px rgba(${a.rgb}, 0.35)`,
          },
        }
      : {
          py: 1,
          backgroundColor: a.main,
          color: a.onMain,
          boxShadow: `0 2px 10px rgba(${a.rgb}, 0.3)`,
          "&:hover": {
            backgroundColor: a.hover,
            color: a.onHover,
            boxShadow: `0 4px 16px rgba(${a.rgb}, 0.42)`,
          },
        }),
  });

  // Keeps the divider line aligned across cards (only Wallet has a note under the CTA)
  const ctaNote = (text) => (
    <Typography
      align="center"
      sx={{ fontFamily: bodyFont, fontSize: "0.74rem", color: colors.ink40, mt: 0.6, minHeight: "18px", lineHeight: 1.25 }}
    >
      {text || "\u00A0"}
    </Typography>
  );

  const getCardSx = (a, isPopular = false) => ({
    width: "100%",
    height: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    backgroundColor: "var(--bv-card-bg, #ffffff)",
    border: isPopular ? `2px solid ${a.main}` : "1.5px solid var(--bv-border, #e2e8f0)",
    borderRadius: "20px",
    p: { xs: 2.25, sm: 2.5, lg: 2, xl: 2.5 },
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "visible",
    boxShadow: isPopular
      ? `0 10px 32px rgba(${a.rgb}, ${isDark ? 0.22 : 0.18})`
      : isDark
        ? "0 4px 20px rgba(0, 0, 0, 0.28)"
        : "0 4px 20px rgba(15, 23, 42, 0.05)",
    transition: "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
    "&:hover": {
      transform: "translateY(-6px)",
      borderColor: `${a.main} !important`,
      boxShadow: `0 16px 40px rgba(${a.rgb}, ${isPopular ? 0.26 : 0.16})`,
    },
  });

  const featureStackSx = { flexGrow: 1, mb: 2 };

  /* ------------------------------------------------------------------ */
  /* Render                                                             */
  /* ------------------------------------------------------------------ */

  return (
    <Box
      className="pricing-2026"
      id="pricing"
      data-reveal
      sx={{ py: { xs: 5, sm: 7, md: 9 }, backgroundColor: colors.bg, scrollMarginTop: "96px" }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        {/* Section Heading — original design preserved */}
        <Typography
          align="center"
          sx={{ fontSize: 12, letterSpacing: "0.1em", color: green.text, fontWeight: 700, mb: 1 }}
        >
          {t("home:pricing.tag")}
        </Typography>
        <Typography
          align="center"
          variant="h3"
          sx={{
            fontWeight: 800,
            color: colors.ink,
            mb: 1.5,
            fontSize: { xs: "clamp(26px, 7.5vw, 36px)", sm: "clamp(34px, 5.5vw, 44px)", md: "clamp(38px, 4vw, 48px)" },
            lineHeight: 1.15,
            letterSpacing: "-0.02em",
          }}
        >
          {t("home:pricing.title1")}
          <br />
          <Box component="span" sx={{ color: green.main }}>
            {t("home:pricing.title2")}
          </Box>
        </Typography>
        <Stack
          direction="row"
          spacing={{ xs: 1.5, sm: 2.5 }}
          justifyContent="center"
          sx={{ mb: { xs: 3.5, sm: 4.5, md: 5 }, flexWrap: "wrap", rowGap: 1 }}
        >
          <Stack direction="row" spacing={0.7} alignItems="center">
            <ShieldIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: colors.ink40 }} />
            <Typography sx={{ color: colors.ink40, fontWeight: 600, fontSize: { xs: 13, sm: 15 } }}>
              {t("home:pricing.trust1")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.7} alignItems="center">
            <BlockIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: colors.ink40 }} />
            <Typography sx={{ color: colors.ink40, fontWeight: 600, fontSize: { xs: 13, sm: 15 } }}>
              {t("home:pricing.trust2")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={0.7} alignItems="center">
            <LockIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: colors.ink40 }} />
            <Typography sx={{ color: colors.ink40, fontWeight: 600, fontSize: { xs: 13, sm: 15 } }}>
              {t("home:pricing.trust3")}
            </Typography>
          </Stack>
        </Stack>
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: { xs: 1, sm: 1.5 },
            p: { xs: 1.4, sm: 1.75 },
            mb: { xs: 3.5, sm: 4.5, md: 5 },
            borderRadius: "10px",
            bgcolor: isDark ? "rgba(24, 180, 236, 0.12)" : "#eaf4fd",
            border: isDark ? "1px solid rgba(24, 180, 236, 0.3)" : "1px solid #d3e9fa",
            maxWidth: "1152px",
            mx: "auto",
          }}
        >
          <LanguageIcon sx={{ color: isDark ? "#38bdf8" : "#1a56db", fontSize: { xs: 26, sm: 32 }, flexShrink: 0 }} />
          <Box sx={{ textAlign: "left", minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: 11.5, sm: 14.5 }, lineHeight: 1.45 }}>
              <Box component="span" sx={{ fontWeight: 800, color: isDark ? "#38bdf8" : "#1a56db" }}>
                {t("home:pricing.international.label")}{" "}
              </Box>
              <Box component="span" sx={{ fontWeight: 700, color: colors.ink }}>
                {t("home:pricing.international.text1")}
              </Box>
            </Typography>
            <Typography sx={{ color: colors.ink40, fontWeight: 500, fontSize: { xs: 10, sm: 12 }, lineHeight: 1.45 }}>
              {t("home:pricing.international.text2")}
            </Typography>
          </Box>
        </Paper>

        {/* Cards grid — equal widths via minmax(0, 1fr) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(4, minmax(0, 1fr))" },
            "@media (min-width: 680px) and (max-width: 1199.95px)": {
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              maxWidth: "860px",
            },
            gap: { xs: 3, sm: 2.5, lg: 2, xl: 2.5 },
            alignItems: "stretch",
            maxWidth: { xs: "440px", lg: "1400px" },
            width: "100%",
            mx: "auto",
            pt: 2,
          }}
        >
          {/* Card 1: Wallet Essentials — GREEN */}
          <Card sx={getCardSx(green)}>
            {renderHeader(
              green,
              AccountBalanceWalletOutlinedIcon,
              t("home:pricing.wallet.name", "Wallet Essentials"),
              t("home:pricing.wallet.subtitle", "Your essential documents, secure and always accessible.")
            )}
            <Box sx={priceBlockSx}>
              <Box sx={{ minHeight: "22px" }} />
              <Box sx={{ minHeight: { xs: "auto", sm: "64px" }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography
                  sx={{
                    fontFamily: headingFont,
                    fontWeight: 800,
                    fontSize: { xs: "1.9rem", lg: "1.65rem", xl: "1.95rem" },
                    color: green.text,
                    lineHeight: 1.15,
                    letterSpacing: "-0.02em",
                    textAlign: "center",
                    wordBreak: "break-word",
                  }}
                >
                  {t("home:pricing.freeForLife", "FREE for Life")}
                </Typography>
              </Box>
              <Box sx={{ my: 0.75, display: "flex", justifyContent: "center", alignItems: "center", minHeight: "32px", width: "100%" }}>
                <Typography component="span" sx={pillSx(green)}>
                  {t("home:pricing.limitedTimeOffer", "Limited-Time Offer")}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontFamily: bodyFont,
                  fontSize: "0.75rem",
                  color: colors.ink60,
                  lineHeight: 1.35,
                  maxWidth: 240,
                  mx: "auto",
                  minHeight: { xs: "auto", sm: "1.2em" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  wordBreak: "break-word",
                }}
              >
                {t("home:pricing.walletFreeSub", "Sign up now and keep Wallet Essentials free for life.")}
              </Typography>
            </Box>
            <Box sx={{ mb: 1.5 }}>
              <Button variant="contained" fullWidth component="a" href={`${urlBase}signup?plan=wallet`} target="_blank" rel="noopener noreferrer" sx={btnSx(green)}>
                {t("home:pricing.getStartedFree", "Get Started Free")}
              </Button>
              {ctaNote(t("home:pricing.noCreditCard", "No credit card required"))}
            </Box>
            <Divider sx={{ mb: 2, borderColor: colors.border }} />
            <Stack spacing={1.5} sx={featureStackSx}>
              {renderFeatureRow(green, t("home:pricing.wallet.features.0", "Up to 15 items"))}
              {renderFeatureRow(green, t("home:pricing.wallet.features.1", "IDs, payment cards, insurance, etc"))}
              {renderFeatureRow(green, t("home:pricing.wallet.features.2", "1 account holder"))}
              {renderFeatureRow(
                green,
                t("home:pricing.wallet.reminderFeature.title", "1 reminder per item"),
                null,
                t("home:pricing.wallet.reminderFeature.sub", "Get a single renewal/expiration alert for each item")
              )}
            </Stack>
            {renderBottom(
              <>
                {renderCallout(
                  green,
                  ShieldIcon,
                  t(
                    "home:pricing.wallet.securityCallout.title",
                    "Same core security as all our plans. End-to-end encryption on your device. Your data is protected by design."
                  ),
                  ""
                )}
                {renderCallout(
                  green,
                  CardGiftcardIcon,
                  t("home:pricing.wallet.bottomCallout.title", "A simple way to get started."),
                  t("home:pricing.wallet.bottomCallout.desc", "Protect your most important documents and information at no cost.")
                )}
              </>
            )}
          </Card>

          {/* Card 2: Personal (Most Popular) — BLUE */}
          <Card sx={getCardSx(blue, true)}>
            <Box
              sx={{
                position: "absolute",
                top: -13,
                left: "50%",
                transform: "translateX(-50%)",
                bgcolor: blue.main,
                color: blue.onMain,
                fontFamily: headingFont,
                fontWeight: 800,
                fontSize: { xs: "0.68rem", sm: "0.7rem" },
                letterSpacing: "0.06em",
                px: { xs: 1.5, sm: 2 },
                py: 0.4,
                borderRadius: "50px",
                boxShadow: `0 4px 14px rgba(${blue.rgb}, 0.45)`,
                whiteSpace: "nowrap",
                maxWidth: "92%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <StarIcon sx={{ fontSize: 13 }} />
              {t("home:pricing.mostPopular", "MOST POPULAR")}
            </Box>
            {renderHeader(
              blue,
              PersonOutlineIcon,
              t("home:pricing.personal.name", "Personal"),
              t("home:pricing.personal.subtitle", "Everything you need for your personal life.")
            )}
            <Box sx={priceBlockSx}>{renderPaidPrice(blue, "personal")}</Box>
            <Box sx={{ mb: 1.5 }}>
              <Button variant="contained" fullWidth component="a" href={`${urlBase}signup?plan=personal`} target="_blank" rel="noopener noreferrer" sx={btnSx(blue)}>
                {t("home:pricing.startFreeTrial", "Start 30-Day Free Trial")}
              </Button>
              {ctaNote()}
            </Box>
            <Divider sx={{ mb: 2, borderColor: colors.border }} />
            <Stack spacing={1.5} sx={featureStackSx}>
              {renderFeatureRow(blue, t("home:pricing.personal.features.0", "Unlimited records"))}
              {renderFeatureRow(blue, t("home:pricing.personal.features.1", "All categories – IDs, properties, insurance, legal, health & more"))}
              {renderFeatureRow(blue, t("home:pricing.personal.features.2", "1 account holder"))}
              {renderFeatureRow(blue, t("home:pricing.personal.features.3", "Unlimited reminders & expiration alerts"))}
              {renderFeatureRow(blue, t("home:pricing.personal.features.4", "Emergency access controls"))}
            </Stack>
            {renderBottom(
              renderCallout(
                blue,
                BoltIcon,
                t("home:pricing.personal.bottomCallout.title", "Fully organized, always prepared."),
                t("home:pricing.personal.bottomCallout.desc", "Keep what matters most secure and accessible.")
              )
            )}
          </Card>

          {/* Card 3: Household — PURPLE */}
          <Card sx={getCardSx(purple)}>
            {renderHeader(
              purple,
              GroupsOutlinedIcon,
              t("home:pricing.household.name", "Household"),
              t("home:pricing.household.subtitle", "Designed for families.")
            )}
            <Box sx={priceBlockSx}>{renderPaidPrice(purple, "household")}</Box>
            <Box sx={{ mb: 1.5 }}>
              <Button variant="contained" fullWidth component="a" href={`${urlBase}signup?plan=household`} target="_blank" rel="noopener noreferrer" sx={btnSx(blue)}>
                {t("home:pricing.startFreeTrial", "Start 30-Day Free Trial")}
              </Button>
              {ctaNote()}
            </Box>
            <Divider sx={{ mb: 2, borderColor: colors.border }} />
            <Stack spacing={1.3} sx={featureStackSx}>
              {renderFeatureRow(purple, t("home:pricing.household.features.0", "Unlimited records"))}
              {renderFeatureRow(purple, t("home:pricing.household.features.1", "All categories for your family"))}
              {renderFeatureRow(purple, t("home:pricing.household.features.2", "Up to 4 account holders"))}
              {renderFeatureRow(purple, t("home:pricing.household.features.3", "Shared access & permissions"))}
              {renderFeatureRow(purple, t("home:pricing.household.features.4", "Unlimited reminders & expiration alerts"))}
              {renderFeatureRow(purple, t("home:pricing.household.features.5", "Emergency access controls"))}
            </Stack>
            {renderBottom(
              <>
                {renderComingSoon(purple, [
                  t("home:pricing.household.comingSoonItems.0", "After-life & legacy planning tools"),
                  t("home:pricing.household.comingSoonItems.1", "Advanced organization & categories"),
                ])}
                {renderCallout(
                  purple,
                  GroupsIcon,
                  t("home:pricing.household.bottomCallout.title", "Stay organized together."),
                  t("home:pricing.household.bottomCallout.desc", "Protect what matters most for your family.")
                )}
              </>
            )}
          </Card>

          {/* Card 4: Groups & Enterprise — ORANGE */}
          <Card sx={getCardSx(orange)}>
            {renderHeader(
              orange,
              ApartmentIcon,
              t("home:pricing.enterprise.name", "Groups & Enterprise"),
              t("home:pricing.enterprise.subtitle", "For organizations, businesses and institutions.")
            )}
            <Box sx={priceBlockSx}>
              <Box sx={{ minHeight: "22px" }} />
              <Box sx={{ minHeight: { xs: "auto", sm: "64px" }, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography
                  sx={{
                    fontFamily: headingFont,
                    fontWeight: 800,
                    fontSize: { xs: "1.9rem", lg: "1.65rem", xl: "1.95rem" },
                    color: colors.ink,
                    lineHeight: 1.15,
                    letterSpacing: "-0.01em",
                    textAlign: "center",
                    wordBreak: "break-word",
                  }}
                >
                  {t("home:pricing.contactSales", "Contact Sales")}
                </Typography>
              </Box>
              <Box sx={{ my: 0.75, minHeight: "32px", width: "100%" }} />
              <Typography
                sx={{
                  fontFamily: bodyFont,
                  fontSize: "0.78rem",
                  color: colors.ink60,
                  lineHeight: 1.35,
                  maxWidth: 240,
                  mx: "auto",
                  minHeight: { xs: "auto", sm: "1.2em" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  wordBreak: "break-word",
                }}
              >
                {t("home:pricing.customPricing", "Custom pricing for your organization")}
              </Typography>
            </Box>
            <Box sx={{ mb: 1.5 }}>
              <Button variant="outlined" fullWidth onClick={handleQuoteClick} sx={btnSx(blue, true)}>
                {t("home:pricing.requestQuote", "Request a Quote")}
              </Button>
              {ctaNote()}
            </Box>
            <Divider sx={{ mb: 2, borderColor: colors.border }} />
            <Stack spacing={1.3} sx={featureStackSx}>
              {renderFeatureRow(orange, t("home:pricing.enterprise.features.0", "Everything in Household"))}
              {renderFeatureRow(orange, t("home:pricing.enterprise.features.1", "100+ accounts (medium to large organizations)"))}
              {renderFeatureRow(
                orange,
                t("home:pricing.enterprise.coBrandingFeature.title", "Co-branding options"),
                t("home:pricing.enterprise.coBrandingFeature.sub", "Add your logo and brand for your members, employees or customers."),
                t("home:pricing.enterprise.coBrandingFeature.sub", "Add your logo and brand for your members, employees or customers.")
              )}
              {renderFeatureRow(orange, t("home:pricing.enterprise.adminFeature", "Centralized administration and user management"))}
            </Stack>
            {renderBottom(
              <>
                {renderComingSoon(orange, [
                  t("home:pricing.enterprise.comingSoonItems.0", "Advanced reporting & compliance tools"),
                  t("home:pricing.enterprise.comingSoonItems.1", "Dedicated support options"),
                  t("home:pricing.enterprise.comingSoonItems.2", "Custom integrations (e.g., SSO, APIs)"),
                ])}
                {renderCallout(
                  orange,
                  BusinessIcon,
                  t("home:pricing.enterprise.bottomCallout.title", "Ideal for credit unions, nonprofits, businesses and institutions."),
                  t("home:pricing.enterprise.bottomCallout.desc", "Bring the power of BeforeVault to your community or organization.")
                )}
              </>
            )}
          </Card>
        </Box>

        {/* Same Core Security strip */}
        <Box
          sx={{
            mt: { xs: 3.5, md: 4.5 },
            maxWidth: { xs: "440px", lg: "1400px" },
            mx: "auto",
            borderRadius: "16px",
            bgcolor: isDark ? "rgba(148, 163, 184, 0.08)" : "#f4f6f8",
            border: isDark ? "1px solid rgba(148, 163, 184, 0.2)" : "1px solid #e5e9ef",
            px: { xs: 2, sm: 2.5, md: 3 },
            py: { xs: 2.5, sm: 3 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(4, 1fr)" },
            gap: { xs: 2.5, sm: 2, lg: 2.5 },
          }}
        >
          {[
            {
              Icon: ShieldIcon,
              title: t("home:pricing.securityStrip.core.title", "Same Core Security Across Every Plan"),
              desc: t(
                "home:pricing.securityStrip.core.desc",
                "The same strong protection for every member — from free to enterprise."
              ),
            },
            {
              Icon: LockIcon,
              title: t("home:pricing.securityStrip.encrypted.title", "Encrypted on Your Device"),
              desc: t(
                "home:pricing.securityStrip.encrypted.desc",
                "Your data is encrypted before it's uploaded to secure cloud storage."
              ),
            },
            {
              Icon: ShieldIcon,
              title: t("home:pricing.securityStrip.access.title", "Secure Account Access"),
              desc: t(
                "home:pricing.securityStrip.access.desc",
                "Multi-factor authentication and additional safeguards."
              ),
            },
            {
              Icon: VisibilityOffOutlinedIcon,
              title: t("home:pricing.securityStrip.privacy.title", "Your Privacy, Always"),
              desc: t(
                "home:pricing.securityStrip.privacy.desc",
                "Your information is never sold or used for advertising."
              ),
            },
          ].map(({ Icon, title, desc }) => (
            <Box key={title} sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  bgcolor: isDark ? "rgba(13, 161, 128, 0.15)" : "#e8f8f3",
                  color: green.text,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontFamily: headingFont,
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    color: colors.ink,
                    lineHeight: 1.3,
                    mb: 0.35,
                  }}
                >
                  {title}
                </Typography>
                <Typography sx={{ fontFamily: bodyFont, fontSize: "0.74rem", color: colors.ink60, lineHeight: 1.4 }}>
                  {desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Bottom Disclaimer */}
        <Typography
          sx={{
            textAlign: "center",
            color: colors.ink40,
            fontFamily: bodyFont,
            fontSize: "0.78rem",
            mt: 3,
            maxWidth: 820,
            mx: "auto",
            lineHeight: 1.5,
          }}
        >
          {t(
            "home:pricing.disclaimer",
            "Coming Soon features are planned enhancements and are not included in current subscriptions. Availability and release dates may vary."
          )}
        </Typography>
      </Container>

      {/* Quote Request Dialog Modal */}
      <Dialog
        open={quoteModalOpen}
        onClose={() => setQuoteModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            p: { xs: 1.5, sm: 2.5 },
            bgcolor: "var(--bv-card-bg, #ffffff)",
            backgroundImage: "none",
            border: "1.5px solid var(--bv-border)",
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontFamily: headingFont, fontWeight: 800, fontSize: "1.25rem", color: colors.ink }}>
            {t("home:pricing.requestQuote", "Request a Quote")}
          </Typography>
          <IconButton onClick={() => setQuoteModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, pt: 1 }}>
          <Typography sx={{ fontFamily: bodyFont, color: colors.ink60, fontSize: "0.85rem", mb: 2.5 }}>
            {t("home:pricing.customPricing", "Custom pricing for your organization")}
          </Typography>
          <Box component="form" onSubmit={handleQuoteSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              required
              fullWidth
              label="Your Name"
              variant="outlined"
              size="small"
              value={quoteForm.name}
              onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
            />
            <TextField
              required
              fullWidth
              label="Work Email"
              type="email"
              variant="outlined"
              size="small"
              value={quoteForm.email}
              onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
            />
            <TextField
              fullWidth
              label="Organization / Company Name"
              variant="outlined"
              size="small"
              value={quoteForm.organization}
              onChange={(e) => setQuoteForm({ ...quoteForm, organization: e.target.value })}
            />
            <TextField
              fullWidth
              label="Estimated Number of Accounts (e.g. 100+, 500+, 2000+)"
              variant="outlined"
              size="small"
              value={quoteForm.accounts}
              onChange={(e) => setQuoteForm({ ...quoteForm, accounts: e.target.value })}
            />
            <TextField
              fullWidth
              label="Additional Notes or Specific Requirements"
              multiline
              rows={3}
              variant="outlined"
              size="small"
              value={quoteForm.message}
              onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={quoteSubmitting}
              sx={{ ...btnSx(blue), mt: 1, py: 1.3, fontSize: "0.95rem" }}
            >
              {quoteSubmitting ? "Submitting..." : t("home:pricing.requestQuote", "Request a Quote")}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default Pricing2026;
